import { useTranslator } from '@bluebeam/react-i18n';
import PageStack from 'components/Icons/PageStack';
import BasicImageModal from 'components/modals/BasicImageModal/BasicImageModal.fc';
import ConfirmModal from 'components/modals/ConfirmModal.fc';
import Truncate from 'components/Truncate';
import React, { useEffect, useState } from 'react';
import { Photo } from 'records/Photo';
import { ProjectId } from 'records/Project';
import { RFIAttachment } from 'records/RFIAttachment';
import { getFile } from 'services/fileService';
import { getPhoto } from 'services/photoService';
import { downloadFileWithAnchor } from 'utils/general';

interface Props {
  projectId: ProjectId;
  attachment: RFIAttachment;
}

const ViewRFIOverlayAttachment: React.FC<Props> = ({
  attachment,
  projectId,
}) => {
  const { translate } = useTranslator(['buttons', 'rfiPage']);
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [modal, setModal] = useState<React.ReactElement | null>(null);

  const loadImage = async (): Promise<void> => {
    const newPhoto = await getPhoto(projectId, attachment.fileid);
    setPhoto(newPhoto);
  };

  useEffect(() => {
    if (attachment.type === 'IMAGE') {
      loadImage();
    }
  }, []);

  const handlePhotoClick = (): void => {
    if (photo) {
      setModal(
        <BasicImageModal
          onDismiss={() => setModal(null)}
          url={photo.url}
          name={photo.filename}
        />
      );
    }
  };

  const downloadFile = async (): Promise<void> => {
    const { url, filename } = await getFile(projectId, attachment.fileid);
    await downloadFileWithAnchor(url, filename);
    setModal(null);
  };

  const handleFileClick = (): void => {
    setModal(
      <ConfirmModal
        title={translate('rfiPage:fileDownload')}
        onDismiss={() => setModal(null)}
        onCancel={() => setModal(null)}
        onConfirm={downloadFile}
        confirmButtonText={translate('buttons:download')}
        cancelButtonText={translate('buttons:cancel')}
      >
        <div>{translate('rfiPage:fileDownloadQuestion')}</div>
        <div className="bold pt3 mb3">
          <Truncate text={attachment.name} />
        </div>
      </ConfirmModal>
    );
  };

  return (
    <>
      {modal}
      <span className="b-radius-2 shrink-0 oh hrem-7p5 wrem-7p5 bgd-gray-3 mr2">
        {photo ? (
          <div
            className="h100 cursor-pointer"
            onClick={handlePhotoClick}
            title={photo.filename}
          >
            <img
              alt={photo.url}
              className="w100 h100 of-cover"
              key={photo.fileid}
              src={photo.url}
            />
          </div>
        ) : null}
        {attachment.type === 'FILE' ? (
          <div
            className="h100 flex-center fill-gray-6 cursor-pointer"
            title={attachment.name}
            onClick={handleFileClick}
          >
            <PageStack width="42px" height="48px" />
          </div>
        ) : null}
      </span>
    </>
  );
};

export default ViewRFIOverlayAttachment;
